import React, { useState, useEffect } from 'react';
import { useNavigate, Link, Outlet, useLocation } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  UserCheck, 
  CreditCard, 
  AlertTriangle,
  Shield,
  Menu,
  X,
  XCircle,
  MessageSquare,
  Megaphone,
  Settings,
  FileText,
  Gift,
  Heart,
  Mail,
  Construction,
  Send,
  Activity,
  Search,
  Image,
  UserCog,
  Download,
  Database,
  Sliders,
  BarChart3,
  FileEdit,
  Bell,
  Calendar,
  Palette,
  Layout,
  Columns,
  Navigation,
  Sparkles,
  ShoppingBag,
  Package,
  Tag,
  ShoppingCart,
  UserX
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminLayout = () => {
  const { user, isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAdminAccess = async () => {
      // Wait for auth context to finish loading
      if (authLoading) {
        return;
      }

      if (!isAuthenticated) {
        navigate('/login');
        return;
      }

      // Check if user is admin from user context first
      if (user && user.isAdmin === true) {
        console.log('Admin access granted via user context');
        setIsAdmin(true);
        setLoading(false);
        return;
      }

      // If user context is loaded but user is not admin, deny access
      if (user && user.isAdmin !== true) {
        console.log('Admin access denied - user is not admin');
        alert('Access Denied: You do not have admin privileges.');
        navigate('/');
        return;
      }

      // If user context is not loaded yet, try API call as fallback
      if (!user) {
        try {
          const token = localStorage.getItem('token');
          if (!token) {
            navigate('/login');
            return;
          }

          console.log('Checking admin access via API call...');
          const response = await axios.get(`${API_URL}/api/admin/stats`, {
            headers: { Authorization: `Bearer ${token}` },
            withCredentials: true
          });
          
          console.log('Admin access granted via API call');
          setIsAdmin(true);
          setLoading(false);
        } catch (error) {
          console.error('Admin access denied:', error.response?.data || error.message);
          // Not an admin, redirect to home
          alert('Access Denied: You do not have admin privileges.');
          navigate('/');
        }
      }
    };

    checkAdminAccess();
  }, [isAuthenticated, navigate, user, authLoading]);

  const menuItems = [
    { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
    { path: '/admin/shop', icon: ShoppingBag, label: 'Shop Management', badge: 'NEW' },
    { path: '/admin/shop/products', icon: Package, label: 'Products' },
    { path: '/admin/shop/categories', icon: Tag, label: 'Categories' },
    { path: '/admin/shop/orders', icon: ShoppingCart, label: 'Orders' },
    { path: '/admin/shop/settings', icon: Settings, label: 'Shop Settings' },
    { path: '/admin/users', icon: Users, label: 'User Management' },
    { path: '/admin/account-approvals', icon: UserCheck, label: 'Account Approvals' },
    { path: '/admin/identity-verifications', icon: Shield, label: 'ID Verifications' },
    { path: '/admin/announcements', icon: Megaphone, label: 'Announcements' },
    { path: '/admin/scheduled-announcements', icon: Calendar, label: 'Scheduled Announcements' },
    { path: '/admin/payment-settings', icon: CreditCard, label: 'Payment Settings' },
    { path: '/admin/trial-reminders', icon: Bell, label: 'Trial Reminders' },
    { path: '/admin/inactive-reminders', icon: UserX, label: 'Inactive Reminders' },
    { path: '/admin/promo-codes', icon: Gift, label: 'Promo Codes' },
    { path: '/admin/success-stories', icon: Heart, label: 'Success Stories' },
    { path: '/admin/email-templates', icon: Mail, label: 'Email Templates' },
    { path: '/admin/bulk-messaging', icon: Send, label: 'Bulk Messaging' },
    { path: '/admin/site-settings', icon: Settings, label: 'Site Settings' },
    { path: '/admin/website-designer', icon: Sparkles, label: 'Website Designer', badge: 'Design' },
    { path: '/admin/content', icon: FileText, label: 'Content Pages' },
    { path: '/admin/seo', icon: Search, label: 'SEO Settings' },
    { path: '/admin/homepage-banners', icon: Image, label: 'Homepage Banners' },
    { path: '/admin/homepage-sections', icon: Layout, label: 'Homepage Sections', badge: 'NEW' },
    { path: '/admin/footer-settings', icon: Columns, label: 'Footer Links' },
    { path: '/admin/navigation-menu', icon: Navigation, label: 'Navigation Menu', badge: 'NEW' },
    { path: '/admin/maintenance', icon: Construction, label: 'Maintenance Mode' },
    { path: '/admin/activity-logs', icon: Activity, label: 'Activity Logs' },
    { path: '/admin/roles', icon: UserCog, label: 'Admin Roles' },
    // Phase 2 Features
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics', badge: 'NEW' },
    { path: '/admin/data-export', icon: Download, label: 'Data Export' },
    { path: '/admin/backups', icon: Database, label: 'Backups' },
    { path: '/admin/matching-algorithm', icon: Sliders, label: 'Matching Algorithm' },
    { path: '/admin/profile-fields', icon: FileEdit, label: 'Profile Fields' },
    { path: '/admin/notification-settings', icon: Bell, label: 'Notification Settings' },
    { path: '/admin/card-designer', icon: Palette, label: 'Profile Card Designer', badge: 'NEW' },
    { path: '/admin/reports', icon: AlertTriangle, label: 'User Reports' },
    { path: '/admin/messages', icon: MessageSquare, label: 'Messages & Support' },
    { path: '/admin/cancellations', icon: XCircle, label: 'Cancellations & Refunds' }
  ];

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-mint-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar */}
      <aside className={`${sidebarOpen ? 'w-64' : 'w-20'} bg-gray-900 text-white transition-all duration-300 flex flex-col`}>
        {/* Logo/Header */}
        <div className="p-4 border-b border-gray-800 flex items-center justify-between">
          {sidebarOpen && <h1 className="text-xl font-bold text-mint-500">Qbool Admin</h1>}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 hover:bg-gray-800 rounded-lg transition-colors"
          >
            {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-2">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                isActive(item.path, item.exact)
                  ? 'bg-mint-500 text-white'
                  : 'text-gray-300 hover:bg-gray-800'
              }`}
            >
              <item.icon className="w-5 h-5" />
              {sidebarOpen && (
                <div className="flex items-center gap-2 flex-1">
                  <span>{item.label}</span>
                  {item.badge && (
                    <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-bold rounded">
                      {item.badge}
                    </span>
                  )}
                </div>
              )}
            </Link>
          ))}
        </nav>

        {/* User Info */}
        <div className="p-4 border-t border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-mint-500 flex items-center justify-center font-bold">
              {user?.firstName?.charAt(0)}{user?.lastName?.charAt(0)}
            </div>
            {sidebarOpen && (
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium truncate">{user?.firstName} {user?.lastName}</p>
                <p className="text-xs text-gray-400">Admin</p>
              </div>
            )}
          </div>
          {sidebarOpen && (
            <Link
              to="/"
              className="mt-3 block text-center text-sm text-gray-400 hover:text-white transition-colors"
            >
              Back to Main Site
            </Link>
          )}
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 overflow-auto">
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
